import type { AutocompleteChangeDetails, AutocompleteChangeReason } from '@mui/material/Autocomplete';
import { useCallback, useMemo } from 'react';

type UseSelectAllDeselectAllProps<T, Value> = {
  handleChange?: (
    event: React.SyntheticEvent,
    value: Value,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
  options?: readonly T[];
  currentValue?: Value;
  showSelectAllOptions?: boolean;
};

export const useSelectAllDeselectAll = <T, Value = T | T[]>({
  handleChange,
  options = [],
  currentValue,
  showSelectAllOptions = false,
}: UseSelectAllDeselectAllProps<T, Value>) => {
  const optionsArray = useMemo(() => Array.from(options), [options]);

  const handleSelectAll = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      handleChange?.(event, optionsArray as Value, 'selectOption');
    },
    [optionsArray, handleChange]
  );

  const handleDeselectAll = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      handleChange?.(event, [] as Value, 'removeOption');
    },
    [handleChange]
  );

  const listboxProps = useMemo(
    () => ({
      showSelectAllOptions,
      options,
      currentValue,
      onSelectAll: handleSelectAll,
      onDeselectAll: handleDeselectAll,
    }),
    [showSelectAllOptions, options, currentValue, handleSelectAll, handleDeselectAll]
  );

  return listboxProps;
};
